/**
 * ValidationSidebar — always-visible summary of catalogue completeness
 * with click-to-navigate.
 *
 * Phase 44.5.
 */

import { useMemo } from "react";
import { hasTodo, capabilityStatus } from "./catalogueHelpers.js";

function summarise(catalogue) {
  const pillars = catalogue?.pillars || [];
  const initiatives = catalogue?.strategic_initiatives || [];
  const issues = [];

  // Pillar count constraint.
  if (pillars.length !== 3) {
    issues.push({
      key: "pillar-count",
      severity: "error",
      message: `Need exactly 3 pillars (currently ${pillars.length})`,
      target: null,
    });
  }

  let totalCapabilities = 0;
  let todoCount = 0;
  // Keep trimmed names; used both for ref-resolution and duplicate
  // detection. Comparison is case-sensitive (matches the backend).
  const capabilityNames = new Set();
  const seenNames = new Set();
  const duplicates = new Set();

  pillars.forEach((pillar, pIdx) => {
    const caps = pillar?.capabilities || [];
    totalCapabilities += caps.length;
    if (caps.length === 0) {
      issues.push({
        key: `pillar-${pIdx}-empty`,
        severity: "warning",
        message: `"${pillar?.label || pillar?.id || `Pillar ${pIdx + 1}`}" has no capabilities`,
        target: { pillar: pIdx },
      });
    }
    caps.forEach((cap, cIdx) => {
      const trimmed = (cap?.name || "").trim();
      if (trimmed) {
        if (seenNames.has(trimmed)) duplicates.add(trimmed);
        seenNames.add(trimmed);
        capabilityNames.add(trimmed);
      }
      const status = capabilityStatus(cap);
      if (status === "todo" || status === "empty") {
        todoCount += 1;
        issues.push({
          key: `cap-${pIdx}-${cIdx}-${status}`,
          severity: status === "empty" ? "warning" : "info",
          message: `"${cap?.name || "Untitled"}" — ${status === "empty" ? "no content" : "has TODO placeholders"}`,
          target: { pillar: pIdx, capability: cIdx },
        });
      }
    });
  });

  // Duplicate capability names make initiative ref resolution ambiguous.
  duplicates.forEach((name) => {
    issues.push({
      key: `dup-${name}`,
      severity: "error",
      message: `Duplicate capability name "${name}" — initiative references are ambiguous`,
      target: null,
    });
  });

  // Initiative checks.
  if (initiatives.length === 0) {
    issues.push({
      key: "no-initiatives",
      severity: "warning",
      message: "No strategic initiatives yet (≥1 required for activation)",
      target: { tab: "initiatives" },
    });
  }
  initiatives.forEach((init, iIdx) => {
    (init?.capabilities_required || []).forEach((name) => {
      // Normalise both sides before comparison — the backend stores
      // untrimmed strings, so whitespace drift would produce false
      // "unknown capability" warnings.
      const normalised = (name || "").trim();
      if (normalised && !capabilityNames.has(normalised)) {
        issues.push({
          key: `init-${iIdx}-unresolved-${name}`,
          severity: "warning",
          message: `"${init.name || `Initiative ${iIdx + 1}`}" references unknown capability "${name}"`,
          target: { tab: "initiatives" },
        });
      }
    });
    if (hasTodo(init?.description)) {
      issues.push({
        key: `init-${iIdx}-todo`,
        severity: "info",
        message: `"${init.name || `Initiative ${iIdx + 1}`}" — description has TODO placeholder`,
        target: { tab: "initiatives" },
      });
    }
  });

  return { pillars, totalCapabilities, todoCount, issues };
}

export default function ValidationSidebar({ catalogue, activeTab, onNavigate }) {
  // Memoise the whole summary so fast typing doesn't re-walk the full
  // pillars × capabilities × fields tree on every keystroke.
  const { pillars, totalCapabilities, todoCount, issues } = useMemo(
    () => summarise(catalogue || {}),
    [catalogue]
  );

  const errorCount = issues.filter((i) => i.severity === "error").length;

  return (
    <aside className="validation-sidebar">
      <h3 className="validation-sidebar__heading">Catalogue completeness</h3>

      <dl className="validation-sidebar__stats">
        <div>
          <dt>Pillars</dt>
          <dd>
            {pillars.length} / 3{" "}
            {pillars.length === 3 ? (
              <span className="validation-sidebar__tick">✓</span>
            ) : (
              <span className="validation-sidebar__cross">✗</span>
            )}
          </dd>
        </div>
        <div>
          <dt>Capabilities</dt>
          <dd>{totalCapabilities}</dd>
        </div>
        <div>
          <dt>TODOs remaining</dt>
          <dd>{todoCount}</dd>
        </div>
      </dl>

      {issues.length === 0 ? (
        <p className="validation-sidebar__empty">All clean — ready to activate.</p>
      ) : (
        <>
          <h4 className="validation-sidebar__subheading">
            {errorCount > 0 ? "Blockers" : "Recommendations"}
          </h4>
          <ul className="validation-sidebar__issues">
            {issues.map((issue) => (
              <li
                key={issue.key}
                className={`validation-sidebar__issue validation-sidebar__issue--${issue.severity}`}
              >
                <button
                  type="button"
                  className="validation-sidebar__jump"
                  onClick={() => onNavigate?.(issue.target)}
                  disabled={!issue.target}
                >
                  {issue.message}
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="validation-sidebar__footer">
        Active: {activeTab === "initiatives" ? "Initiatives" : `Pillar ${activeTab + 1}`}
      </div>
    </aside>
  );
}
